import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { applicationsAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { 
  Briefcase, Building, MapPin, Clock, ChevronRight,
  Loader2, FileText, CheckCircle, XCircle, Calendar
} from 'lucide-react';

const STAGE_CONFIG = {
  applied: { label: 'Applied', color: 'bg-gray-500', icon: FileText },
  screening: { label: 'Screening', color: 'bg-yellow-500', icon: FileText },
  scheduled: { label: 'Interview Scheduled', color: 'bg-blue-500', icon: Calendar },
  interview: { label: 'Interview', color: 'bg-purple-500', icon: Calendar },
  feedback: { label: 'Under Review', color: 'bg-indigo-500', icon: FileText },
  offer: { label: 'Offer Received', color: 'bg-green-500', icon: CheckCircle },
  hired: { label: 'Hired', color: 'bg-emerald-600', icon: CheckCircle },
  rejected: { label: 'Not Selected', color: 'bg-red-500', icon: XCircle },
};

const MyApplications = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const role = localStorage.getItem('userRole');
    
    if (!token || role !== 'job_seeker') {
      toast({
        title: 'Access Denied',
        description: 'Please login as a job seeker to view your applications',
        variant: 'destructive',
      });
      navigate('/login');
      return;
    }
    
    fetchApplications();
  }, [navigate]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await applicationsAPI.getMyApplications();
      setApplications(response.data || []);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast({
        title: 'Error',
        description: 'Failed to load your applications',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getStageConfig = (stage) => {
    return STAGE_CONFIG[stage] || STAGE_CONFIG.applied;
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Applications</h1>
            <p className="text-gray-600 mt-1">Track the status of your job applications</p>
          </div>
          <Button 
            onClick={() => navigate('/')}
            variant="outline"
            className="border-blue-500 text-blue-600 hover:bg-blue-50"
          >
            Browse Jobs
          </Button>
        </div>

        {applications.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">No Applications Yet</h2>
            <p className="text-gray-500 mb-6">
              You haven't applied to any jobs yet. Start exploring opportunities!
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="browse-jobs-btn"
            >
              <Briefcase className="w-4 h-4 mr-2" />
              Browse Jobs
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {applications.map((app) => {
              const stageConfig = getStageConfig(app.currentStage);
              const StageIcon = stageConfig.icon;
              
              return (
                <Card 
                  key={app.id}
                  className="p-6 hover:shadow-md transition-shadow"
                  data-testid={`application-card-${app.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {app.jobTitle}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-xs text-white flex items-center gap-1 ${stageConfig.color}`}>
                          <StageIcon className="w-3 h-3" />
                          {stageConfig.label}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                        <span className="flex items-center gap-1">
                          <Building className="w-4 h-4" />
                          {app.company}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          Applied: {formatDate(app.appliedAt)}
                        </span>
                      </div>

                      {/* Stage History Preview */}
                      {app.stageHistory && app.stageHistory.length > 0 && (
                        <div className="flex items-center gap-2 mt-3">
                          {app.stageHistory.slice(-3).map((history, idx) => (
                            <span 
                              key={idx}
                              className="text-xs text-gray-500 flex items-center gap-1"
                            >
                              <span className={`w-2 h-2 rounded-full ${getStageConfig(history.stage).color}`} />
                              {getStageConfig(history.stage).label}
                              {idx < Math.min(app.stageHistory.length, 3) - 1 && (
                                <ChevronRight className="w-3 h-3 text-gray-400" />
                              )}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Offer Details */}
                      {app.currentStage === 'offer' && app.offerDetails && (
                        <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-sm font-medium text-green-800">
                            Offer Details: {app.offerDetails.salary}
                          </p>
                          {app.offerDetails.joiningDate && (
                            <p className="text-xs text-green-600 mt-1">
                              Joining Date: {app.offerDetails.joiningDate}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Interview Schedule */}
                      {app.currentStage === 'scheduled' && app.interviews && app.interviews.length > 0 && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-sm font-medium text-blue-800">
                            Interview: {app.interviews[app.interviews.length - 1].interviewType}
                          </p>
                          <p className="text-xs text-blue-600 mt-1">
                            {formatDate(app.interviews[app.interviews.length - 1].scheduledAt)}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="text-right">
                      <p className="text-xs text-gray-400">
                        Last updated: {formatDate(app.updatedAt)}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default MyApplications;
